import React, { useMemo, useState } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Search, GripVertical } from 'lucide-react';
import { Guest } from '@/types';

interface UnassignedGuestsPanelProps {
  guests: Guest[];
}

interface DraggableGuestChipProps {
  guest: Guest;
}

const DraggableGuestChip: React.FC<DraggableGuestChipProps> = ({ guest }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `guest:${guest.id}`,
    data: { guestId: guest.id },
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={`flex items-center gap-2 px-2 py-1.5 border rounded bg-white hover:bg-gray-50 cursor-grab ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <GripVertical className="w-3 h-3 text-gray-400 flex-shrink-0" />
      <span className="text-sm truncate flex-1" title={guest.nombre}>
        {guest.nombre}
      </span>
      {guest.numero_acompanantes > 0 && (
        <Badge variant="secondary" className="flex-shrink-0">
          +{guest.numero_acompanantes}
        </Badge>
      )}
    </div>
  );
};

export const UnassignedGuestsPanel: React.FC<UnassignedGuestsPanelProps> = ({ guests }) => {
  const [search, setSearch] = useState('');
  const [includeNonConfirmed, setIncludeNonConfirmed] = useState(false);

  const { setNodeRef, isOver } = useDroppable({
    id: 'mesa:unassigned',
    data: { mesaId: null },
  });

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return guests.filter((g) => {
      if (!includeNonConfirmed && g.estado !== 'CONFIRMADO') return false;
      if (term && !g.nombre.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [guests, search, includeNonConfirmed]);

  return (
    <Card className={isOver ? 'ring-2 ring-cyan-500' : ''}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Invitados sin mesa ({filtered.length})</CardTitle>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar invitado..."
            className="pl-8"
          />
        </div>
        <div className="flex items-center justify-between pt-1">
          <Label htmlFor="include-non-conf" className="text-xs text-gray-600 cursor-pointer">
            Incluir pendientes/rechazados
          </Label>
          <Switch
            id="include-non-conf"
            checked={includeNonConfirmed}
            onCheckedChange={setIncludeNonConfirmed}
          />
        </div>
      </CardHeader>
      <CardContent ref={setNodeRef} className="space-y-1 max-h-[600px] overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">
            {guests.length === 0
              ? 'Todos los invitados están asignados.'
              : 'No hay invitados con ese filtro.'}
          </p>
        ) : (
          filtered.map((g) => <DraggableGuestChip key={g.id} guest={g} />)
        )}
      </CardContent>
    </Card>
  );
};
